"use client";

import { useId, type HTMLAttributes, type HTMLInputTypeAttribute } from "react";

type OutlinedInputProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  autoComplete?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  min?: number;
  max?: number;
  disabled?: boolean;
  name?: string;
};

export function OutlinedInput({
  label,
  value,
  onChange,
  required,
  type = "text",
  autoComplete,
  inputMode,
  min,
  max,
  disabled,
  name,
}: OutlinedInputProps) {
  const id = useId();
  const hasValue = value.length > 0;

  return (
    <div className="relative">
      <input
        id={id}
        name={name}
        required={required}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        min={min}
        max={max}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=" "
        className="
          peer
          w-full rounded-xl
          border border-gray-300
          bg-white
          px-4 pt-6 pb-3
          text-sm text-gray-900
          transition-[border-color,box-shadow,transform,background-color] duration-300 ease-out
          will-change-transform
          focus:outline-none
          focus:border-amber-500
          focus:-translate-y-[1px]
          focus:bg-white
          focus:shadow-[0_0_0_4px_rgba(212,175,102,0.14)]
          disabled:cursor-not-allowed
          disabled:bg-gray-50
          disabled:text-gray-400
        "
      />

      <label
        htmlFor={id}
        className={`
          pointer-events-none
          absolute left-3
          bg-white px-2
          transition-all duration-300 ease-out
          ${hasValue ? "-top-2 text-xs text-amber-600" : "top-4 text-sm text-gray-500"}
          peer-focus:-top-2
          peer-focus:text-xs
          peer-focus:text-amber-600
        `}
      >
        {label}
      </label>
    </div>
  );
}
